import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extension-placeholder'
import { marked } from 'marked'

/* ─── Markdown serializer ────────────────────────────────────────────────── */

function marksWrap(text, marks) {
  if (!marks || !marks.length) return text
  let out = text
  marks.forEach(m => {
    if (m.type === 'bold') out = `**${out}**`
    else if (m.type === 'italic') out = `*${out}*`
    else if (m.type === 'code') out = `\`${out}\``
  })
  return out
}

function inlineToMd(nodes) {
  if (!nodes) return ''
  return nodes
    .map(n => {
      if (n.type === 'text') return marksWrap(n.text || '', n.marks)
      if (n.type === 'hardBreak') return '  \n'
      return inlineToMd(n.content)
    })
    .join('')
}

function nodeToMd(node, listCtx) {
  const c = node.content || []
  switch (node.type) {
    case 'doc':
      return c
        .map(n => nodeToMd(n))
        .filter(Boolean)
        .join('\n\n')
    case 'paragraph':
      return inlineToMd(c)
    case 'heading': {
      const lvl = (node.attrs && node.attrs.level) || 1
      return `${'#'.repeat(lvl)} ${inlineToMd(c)}`
    }
    case 'bulletList':
      return c.map(n => nodeToMd(n, 'bullet')).join('\n')
    case 'orderedList': {
      let i = (node.attrs && node.attrs.start) || 1
      return c
        .map(n => {
          const md = nodeToMd(n, i)
          i++
          return md
        })
        .join('\n')
    }
    case 'listItem': {
      const prefix = typeof listCtx === 'number' ? `${listCtx}. ` : '- '
      return (
        prefix +
        c
          .map(n => nodeToMd(n))
          .join('\n')
          .trim()
      )
    }
    case 'blockquote':
      return c
        .map(n => nodeToMd(n))
        .join('\n')
        .split('\n')
        .map(l => `> ${l}`)
        .join('\n')
    case 'codeBlock': {
      const lang = (node.attrs && node.attrs.language) || ''
      return `\`\`\`${lang}\n${c.map(n => n.text || '').join('')}\n\`\`\``
    }
    case 'horizontalRule':
      return '---'
    default:
      return inlineToMd(c)
  }
}

function tiptapJsonToMarkdown(json) {
  if (!json || json.type !== 'doc') return ''
  return nodeToMd(json).trim()
}

/* ─── Toolbar ────────────────────────────────────────────────────────────── */

const tbStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  padding: '4px 8px',
  borderBottom: '1px solid #ddd',
  background: '#f5f5f5',
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4,
  flexWrap: 'wrap',
}
const btnBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 28,
  height: 28,
  padding: '0 5px',
  border: '1px solid transparent',
  borderRadius: 3,
  background: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: 'inherit',
  color: '#444',
  outline: 'none',
}
const btnActive = { ...btnBase, background: '#dde', border: '1px solid #aab' }
const sep = (
  <span
    style={{ display: 'inline-block', width: 1, height: 18, background: '#ccc', margin: '0 3px' }}
  />
)

function Btn({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      title={title}
      style={active ? btnActive : btnBase}
      onMouseDown={e => {
        e.preventDefault()
        onClick()
      }}
    >
      {children}
    </button>
  )
}

function Toolbar({ editor }) {
  if (!editor) return null
  return (
    <div style={tbStyle}>
      <Btn
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleBold()
            .run()
        }
        active={editor.isActive('bold')}
        title="Bold"
      >
        <strong>B</strong>
      </Btn>
      <Btn
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleItalic()
            .run()
        }
        active={editor.isActive('italic')}
        title="Italic"
      >
        <em>I</em>
      </Btn>
      {sep}
      <Btn
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 1 })
            .run()
        }
        active={editor.isActive('heading', { level: 1 })}
        title="Heading 1"
      >
        H1
      </Btn>
      <Btn
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 2 })
            .run()
        }
        active={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        H2
      </Btn>
      <Btn
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 3 })
            .run()
        }
        active={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        H3
      </Btn>
      {sep}
      <Btn
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleBulletList()
            .run()
        }
        active={editor.isActive('bulletList')}
        title="Bullet list"
      >
        ≡
      </Btn>
      <Btn
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleOrderedList()
            .run()
        }
        active={editor.isActive('orderedList')}
        title="Numbered list"
      >
        1.
      </Btn>
      {sep}
      <Btn
        onClick={() =>
          editor
            .chain()
            .focus()
            .undo()
            .run()
        }
        active={false}
        title="Undo"
      >
        ↩
      </Btn>
      <Btn
        onClick={() =>
          editor
            .chain()
            .focus()
            .redo()
            .run()
        }
        active={false}
        title="Redo"
      >
        ↪
      </Btn>
    </div>
  )
}

/* ─── Editor ─────────────────────────────────────────────────────────────── */

/**
 * WYSIWYG rich-text editor backed by Tiptap.
 * Accepts and emits plain markdown — no markdown syntax visible while editing.
 * Uses `marked` (CJS, webpack-4-safe) to parse markdown → HTML on load.
 * Serialises Tiptap's JSON doc back to markdown on every change.
 */
const WipEasyMdeField = ({ value, onChange, options }) => {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const skipUpdateRef = useRef(false)
  const lastExternalRef = useRef(value || '')

  const placeholder = (options && options.placeholder) || 'Type your summary here…'

  const editor = useEditor({
    extensions: [StarterKit, Placeholder.configure({ placeholder })],
    content: value ? marked.parse(value) : '',
    onUpdate({ editor: e }) {
      if (skipUpdateRef.current) {
        skipUpdateRef.current = false
        return
      }
      const md = tiptapJsonToMarkdown(e.getJSON())
      lastExternalRef.current = md
      onChangeRef.current(md)
    },
  })

  useEffect(() => {
    if (!editor) return
    const next = value || ''
    if (lastExternalRef.current !== next) {
      lastExternalRef.current = next
      skipUpdateRef.current = true
      editor.commands.setContent(next ? marked.parse(next) : '', false)
    }
  }, [value, editor])

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 4, overflow: 'hidden' }}>
      <Toolbar editor={editor} />
      <div
        style={{
          minHeight: 160,
          padding: '10px 14px',
          fontSize: '0.95rem',
          lineHeight: 1.65,
          fontFamily: 'inherit',
          color: '#212121',
          cursor: 'text',
        }}
        onClick={() => {
          if (editor) editor.commands.focus()
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

WipEasyMdeField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.object,
}

WipEasyMdeField.defaultProps = {
  value: '',
  options: {},
}

export default WipEasyMdeField
